import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardBody, Collapse, Nav, NavItem, NavLink, Button } from 'reactstrap';
import classnames from 'classnames';
import { appLanguages } from '../../common/languages';
import { langFlags } from '../../common/langFlags';
import RefreshButton from '../../components/Common/RefreshButton';
import LocalButton from '../../components/Common/Button';
import NewVoiceForm from './NewVoiceForm';
import { regenerateVoiceSample } from '../../helpers/API/assistant';
import { removeVoice, addVoiceToLanguage } from '../../helpers/API/content/voices';
import { localeMap } from '../../common/languages';

const VoicesBlock = ({ voices, fetchVoices, isLoading }) => {
  const [activeTab, setActiveTab] = useState('en');
  const [collapse, setCollapse] = useState([]);
  const [addingVoice, setAddingVoice] = useState(false);
  const [generatingSample, setGeneratingSample] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [messageType, setMessageType] = useState('error');
  const [cloneDropdownOpen, setCloneDropdownOpen] = useState(null);
  const [cloning, setCloning] = useState(null);

  const toggleTab = tab => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setCollapse([]);
    }
  };

  const toggleCollapse = index => {
    setCollapse(collapse => {
      const newCollapse = [...collapse];
      newCollapse[index] = !newCollapse[index];
      return newCollapse;
    });
  };

  const handleRegenerateAudio = async (voice) => {
    try {
      setGeneratingSample(voice.name);
      console.log('🔄 Starting regeneration for voice:', voice.name, 'in language:', activeTab);

      const response = await regenerateVoiceSample(voice, activeTab);

      if (response && (response.fileUrl || response.data?.fileUrl)) {
        const audioURL = response.fileUrl || response.data.fileUrl;
        // Update the voice object with the new sampleAudioURL
        voice.sampleAudioURL = audioURL;
        fetchVoices(activeTab);  // Refetch voices to get the updated sample URL

        setMessageType('success');
        setErrorMessage(`Audio sample regenerated successfully for ${voice.displayName}!`);
        setTimeout(() => setErrorMessage(''), 3000);

        console.log('✅ Voice sample regenerated successfully:', audioURL);
      } else {
        console.warn('⚠️ Regeneration response missing fileUrl:', response);
        setMessageType('error');
        setErrorMessage('Audio regenerated but no file URL returned.');
        setTimeout(() => setErrorMessage(''), 5000);
      }
    } catch (error) {
      console.error('❌ Error regenerating voice sample:', error);
      setMessageType('error');

      // More specific error messages
      if (error.message?.includes('Response is missing data field')) {
        setErrorMessage('TTS service response format error. Please try again or contact support.');
      } else if (error.message?.includes('No audio file URL')) {
        setErrorMessage('Audio generation failed - no file was created.');
      } else {
        setErrorMessage(`Error regenerating voice sample: ${error.message || 'Unknown error'}`);
      }

      setTimeout(() => setErrorMessage(''), 7000);
    } finally {
      setGeneratingSample(null);
    }
  };

  const handleRemoveVoice = async (voice) => {
    if (window.confirm(`Are you sure you want to remove the voice: ${voice.name}?`)) {
      try {
        await removeVoice(activeTab, voice.name);
        fetchVoices(activeTab);  // Refetch voices to get the updated list
      } catch (error) {
        console.error('Error removing voice:', error);
        setMessageType('error');
        setErrorMessage('Error removing voice.');
        setTimeout(() => setErrorMessage(''), 5000);
      }
    }
  };

  const handleCloneVoice = async (voice, targetLanguage) => {
    setCloning(voice.name);
    setCloneDropdownOpen(null);

    try {
      // Create a clone of the voice with updated locale
      const clonedVoice = {
        ...voice,
        locale: localeMap[targetLanguage] ? localeMap[targetLanguage][0] : `${targetLanguage}-${targetLanguage.toUpperCase()}`
      };

      // Add the cloned voice to the target language
      await addVoiceToLanguage(targetLanguage, clonedVoice);

      // Refresh voices for the target language if it's the current active tab
      if (targetLanguage === activeTab) {
        fetchVoices(activeTab);
      }

      console.log(`Voice "${voice.name}" cloned successfully to language "${targetLanguage}"`);
      setMessageType('success');
      setErrorMessage(`Voice "${voice.displayName}" cloned successfully to ${targetLanguage.toUpperCase()}!`);
      setTimeout(() => setErrorMessage(''), 3000);

    } catch (error) {
      console.error('Error cloning voice:', error);
      setMessageType('error');
      setErrorMessage('Error cloning voice.');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setCloning(null);
    }
  };

  const toggleCloneDropdown = (voiceName) => {
    setCloneDropdownOpen(cloneDropdownOpen === voiceName ? null : voiceName);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cloneDropdownOpen && !event.target.closest('.clone-dropdown-container')) {
        setCloneDropdownOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [cloneDropdownOpen]);

  return (
    <div className="white-block flex-2">
      <h2>Voices</h2>
      <LocalButton
        text="Add Voice"
        icon="bx bx-plus"
        extraClass={addingVoice ? 'active' : ''}
        onClick={() => setAddingVoice(!addingVoice)}
      />
      <RefreshButton
        isLoading={isLoading[activeTab]}
        onClick={() => fetchVoices(activeTab)} />
      <Nav tabs>
        {appLanguages.map(lang => (
          <NavItem key={lang}>
            <NavLink
              className={classnames({ active: activeTab === lang })}
              onClick={() => { toggleTab(lang); }}
            >
              <img
                src={langFlags[lang]}
                alt={`${lang} flag`}
                style={{
                  width: '20px',
                  height: '20px',
                  marginRight: '5px'
                }}
              />
              {lang.charAt(0).toUpperCase() + lang.slice(1)}
            </NavLink>
          </NavItem>
        ))}
      </Nav>
      {isLoading[activeTab] ? (
        <div className="loading-container">
          <div className="spinner-border text-primary"></div>
        </div>
      ) : (
        voices[activeTab]?.map((voice, index) => (
          <Card key={index}>
            <CardHeader
              onClick={() => toggleCollapse(index)}
              className={
                collapse[index] ? 'active' : ''
              }
            >
              {voice.name}
            </CardHeader>
            <Collapse isOpen={collapse[index]}>
              <CardBody>
                <p><span>Display Name:</span> {voice.displayName}</p>
                <p><span>Gender:</span> {voice.gender}</p>
                <p><span>Locale:</span> {voice.locale}</p>
                {voice.voiceType && <p><span>Voice Type:</span> {voice.voiceType}</p>}
                <p><span>Words Per Minute:</span> {voice.wordsPerMinute}</p>
                <p><span>Type:</span> {voice.type}</p>
                <p><span>Category:</span> {voice.category}</p>
                <p>
                  <span className="me-2">Sample Audio URL:</span>
                  <button className="button" onClick={() => window.open(voice.sampleAudioURL, '_blank')}>
                    <i className="mdi mdi-play-circle me-1"></i>
                    Listen
                  </button>
                </p>
                <p>
                  <audio controls src={voice.sampleAudioURL} style={{ marginRight: 'auto' }}></audio>
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
                                    <Button onClick={() => handleRegenerateAudio(voice)} color="primary" size="sm">
                    {generatingSample === voice.name ? <div className="spinner-border spinner-border-sm text-light"></div> : 'Regenerate Audio Sample'}
                  </Button>
                  <Button onClick={() => handleRemoveVoice(voice)} color="danger" size="sm">
                    Remove Voice
                  </Button>
                  <div className="clone-dropdown-container" style={{ position: 'relative', display: 'inline-block' }}>
                    <Button
                      onClick={() => toggleCloneDropdown(voice.name)}
                      color="info"
                      size="sm"
                      disabled={cloning === voice.name}
                    >
                      {cloning === voice.name ? (
                        <div className="spinner-border spinner-border-sm text-light"></div>
                      ) : (
                        '🔗 Clone to Language'
                      )}
                    </Button>
                  {cloneDropdownOpen === voice.name && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      backgroundColor: 'white',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      zIndex: 1000,
                      minWidth: '150px',
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}>
                      {appLanguages.filter(lang => lang !== activeTab).map(lang => (
                        <button
                          key={lang}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: 'none',
                            backgroundColor: 'transparent',
                            textAlign: 'left',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          onClick={() => handleCloneVoice(voice, lang)}
                        >
                          <img
                            src={langFlags[lang]}
                            alt={`${lang} flag`}
                            style={{
                              width: '16px',
                              height: '16px',
                              marginRight: '8px'
                            }}
                          />
                          {lang.toUpperCase()}
                        </button>
                      ))}
                                         </div>
                   )}
                 </div>
                </div>
                {errorMessage && (
                  <div
                    className={messageType === 'success' ? 'success-message' : 'error-message'}
                    style={{
                      marginTop: '10px',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: '500',
                      backgroundColor: messageType === 'success' ? '#d4edda' : '#f8d7da',
                      color: messageType === 'success' ? '#155724' : '#721c24',
                      border: messageType === 'success' ? '1px solid #c3e6cb' : '1px solid #f5c6cb'
                    }}
                  >
                    {errorMessage}
                  </div>
                )}
              </CardBody>
            </Collapse>
          </Card>
        ))
      )}
      {addingVoice && (
        <NewVoiceForm
          fetchVoices={fetchVoices}
          voices={voices}
        />
      )}
    </div>
  );
};

export default VoicesBlock;
